import {
  AIType,
  type GenerateBriefBody,
  generateBriefResponseSchema,
  type ValidateBriefBody,
  validateBriefResponseSchema,
  type GenerateCommentsRequest,
  generatePromptResponseSchema,
  type AskPunttRequest,
  askPunttResponseSchema,
} from "@mg/schemas/src/ai/ai";
import {
  type CreateTicketRequest,
  createTicketResponseSchema,
  generateMoodBoardImagesResponseSchema,
  getBriefTemplatesResponseSchema,
  getTicketResponseSchema,
  type UpdateTicketBody,
  updateTicketResponseSchema,
  type CreateTicketCommentBody,
  createTicketCommentResponseSchema,
  getTicketCommentsResponseSchema,
  type UpdateTicketCommentBody,
  updateTicketCommentResponseSchema,
  addReviewerToRevisionBoardResponseSchema,
  type ReviewTicketBody,
  reviewTicketResponseSchema,
  type SendToDesignBody,
  sendToDesignResponseSchema,
  getDownloadDeliveryResponseSchema,
  suggestCreativesResponseSchema,
  updateRevisionBoardResponseSchema,
  suggestReviewersResponseSchema,
  shareTicketResponseSchema,
  getTicketsPaginatedResponseSchema,
  type CreateFolderBody,
  createFolderResponseSchema,
  getFolderTicketsPaginatedResponseSchema,
  UpdateFolderBody,
  ShareTicketBody,
  ShareTicketParams,
  downloadTicketResponseSchema,
  CombineTicketsBody,
  softDeleteRevisionBoardResponseSchema,
  AddBoardToRevisionBoardBody,
  addBoardToRevisionBoardResponseSchema,
  getRevisionStatusResponseSchema,
  type GetFolderTicketsPaginatedResponse,
  getFolderDetailsResponseSchema,
} from "@mg/schemas/src/christo/catalyst";
import { ticketParticipantsSchema } from "@mg/schemas/src/commons";
import { z } from "zod";

import { LEGACY_AI_URL, API_URL } from "../config/env";
import { withReadableCitations } from "../utils/citationRepair";
import {
  EnterpriseMfaStepParkedError,
  isEnterpriseMfaStep,
  mfaOptionsOf,
  type EnterpriseMfaStep,
} from "../utils/enterpriseMfaStep";
import { smartFetch, getAuthToken } from "../utils/http";
import { parseResponse } from "../utils/parseResponse";
import { openEnterpriseMfaSwitch } from "../utils/slices/ui";
import { store } from "../utils/store";

interface QueryParams {
  [key: string]: string | string[] | number | boolean | undefined;
}

/**
 * The MFA step the share-link auto-switch can answer a resource GET with,
 * parked so `AuthenticationDialog` opens on it.
 *
 * `possiblySwitchEnterpriseProfile` runs on `GET /private/catalyst/tickets/:id`
 * and `GET /private/catalyst/folders/:id`, and a share link into an enterprise
 * the caller belongs to but is not scoped to is answered with the step arm, as
 * a 200, instead of the resource. The step has no token — dispatching `login`
 * with it would decode `undefined` — so it goes to the same `ui` slot
 * `switchEnterpriseWithMfa` parks the switch route's step in, and this is the
 * only reader of that slot that is not a switch.
 *
 * Dispatched here rather than at the call sites: ten callers read these
 * endpoints, and a step that reached any one of them unprepared is the
 * silently-dropped switch this exists to prevent. `services/userProfile` already
 * dispatches through the store the same way, and `utils/store` is in this
 * module's graph already (`utils/http` imports it).
 */
function parkEnterpriseMfaStep(step: EnterpriseMfaStep): void {
  store.dispatch(openEnterpriseMfaSwitch(mfaOptionsOf(step)));
}

export async function getProjects() {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/paginated`,
    {
      method: "POST",
    },
  );

  const parsedData = parseResponse(getTicketsPaginatedResponseSchema, json);
  return parsedData;
}

export async function getProject(
  ticketId: string,
  options?: { confirmJoin?: boolean },
) {
  const urlToken = localStorage.getItem("urlToken");
  const params = new URLSearchParams();
  if (urlToken) {
    params.set("t", urlToken);
  }
  if (options?.confirmJoin) {
    params.set("confirmJoin", "1");
  }
  const query = params.toString();
  const tokenParam = query ? `?${query}` : "";
  const token = sessionStorage.getItem(`${ticketId}|token`);

  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}${tokenParam}`,
    {
      method: "GET",
    },
    false,
    { token },
  );

  // Before the ticket schema and before `urlToken` is cleared. The body is the
  // TOTP step of a share-link switch, not a ticket: parsing it reported a
  // `ZodError` on a ticket payload, and clearing the share token first left the
  // retry that finishes the switch with nothing to present — the caller is not
  // in the target enterprise until the code clears, so `urlToken` is still what
  // opens this link. Park the step and let `AuthenticationDialog` complete it;
  // the success path reloads, this read runs again, and by then the caller is
  // scoped to the target (`needsToSwitch` false) so the ticket comes back.
  if (isEnterpriseMfaStep(json)) {
    parkEnterpriseMfaStep(json);
    throw new EnterpriseMfaStepParkedError(json);
  }

  // Only clear after a successful fetch so a 409 join-confirm retry can reuse it.
  localStorage.removeItem("urlToken");
  const extendedSchema = getTicketResponseSchema.extend({
    markup: z.string().optional(),
  });
  const parsedData = extendedSchema.parse(json);

  return parsedData;
}

export async function getTicketVersionStatus({
  ticketId,
  versionId,
}: {
  ticketId: string;
  versionId: string;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/revisions/${versionId}/status`,
    {
      method: "GET",
    },
  );

  const parsedData = getRevisionStatusResponseSchema.parse(json);
  return parsedData;
}

export async function createProject(payload: CreateTicketRequest["body"]) {
  const json = await smartFetch(`${API_URL}/private/catalyst/tickets`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const parsedData = createTicketResponseSchema.parse(json);
  return parsedData;
}

export async function updateProject(payload: UpdateTicketBody) {
  const json = await smartFetch(`${API_URL}/private/catalyst/tickets`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  const parsedData = updateTicketResponseSchema.parse(json);
  return parsedData;
}

export async function getBriefTemplates() {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/brief-templates`,
    {
      method: "GET",
    },
  );

  const parsedData = getBriefTemplatesResponseSchema.parse(json);
  return parsedData;
}

export async function generateBrief(payload: GenerateBriefBody) {
  const json = await smartFetch(
    `${LEGACY_AI_URL}/?type=${AIType.TICKET_BRIEF}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    true,
  );

  const parsedData = generateBriefResponseSchema.parse(json);
  return parsedData;
}

export async function validateBrief({
  payload,
}: {
  payload: ValidateBriefBody;
}) {
  const json = await smartFetch(
    `${LEGACY_AI_URL}/?type=${AIType.TICKET_VALIDATE_BRIEF}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    true,
  );

  const parsedData = validateBriefResponseSchema.parse(json);
  return parsedData;
}

export async function generateMoodBoard(description: string) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/generate-moodboard`,
    {
      method: "POST",
      body: JSON.stringify({ description }),
    },
  );

  const parsedData = generateMoodBoardImagesResponseSchema.parse(json);
  return parsedData;
}

export async function fetchTicketComments({
  ticketId,
}: {
  ticketId: string;
  query: string;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/comments`,
    {
      method: "GET",
    },
  );

  // A citation this client cannot read must cost the chip, not the ticket:
  // `.parse` on the raw payload threw, and the React Router error boundary
  // turned that throw into a blank ticket view (58 prod events in the 24h to
  // 2026-09-24). Same treatment the ask-v2 review path applies -- see
  // `utils/citationRepair`.
  const parsedData = getTicketCommentsResponseSchema.parse(
    withReadableCitations(json),
  );
  return parsedData;
}

export async function createComment({
  ticketId,
  payload,
}: {
  ticketId: string;
  payload: CreateTicketCommentBody;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/comments`,
    {
      method: "POST",
      body: JSON.stringify({
        ...payload,
      }),
    },
  );

  const parsedData = createTicketCommentResponseSchema.parse(json);
  return parsedData;
}

export async function editComment({
  ticketId,
  messageId,
  payload,
}: {
  ticketId: string;
  messageId: string;
  payload: UpdateTicketCommentBody;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/comments/${messageId}`,
    {
      method: "PUT",
      body: JSON.stringify({
        ...payload,
      }),
    },
  );

  const parsedData = updateTicketCommentResponseSchema.parse(json);
  return parsedData;
}

export async function deleteCommentMessage({
  ticketId,
  messageId,
}: {
  ticketId: string;
  messageId: string;
}) {
  await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/comments/${messageId}`,
    {
      method: "DELETE",
    },
  );

  return undefined;
}

export class ReviewAlreadyInProgressError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ReviewAlreadyInProgressError";
  }
}

/**
 * The browser's poll budget ran out while the review workflow was still
 * running. The backend keeps going and saves its comments when it finishes,
 * so callers should treat this as "still running", not as a failure.
 */
export class ReviewStillRunningError extends Error {
  readonly workflowId: string;

  constructor(message: string, workflowId: string) {
    super(message);
    this.name = "ReviewStillRunningError";
    this.workflowId = workflowId;
  }
}

/**
 * The review workflow finished with a failed result. `detail` is the backend's
 * own error, often a Temporal cause chain: fine for analytics, not for a toast.
 */
export class AIReviewFailedError extends Error {
  readonly detail: string;

  constructor(detail: string) {
    super(`AI review failed: ${detail}`);
    this.name = "AIReviewFailedError";
    this.detail = detail;
  }
}

const aiReviewStatusSchema = z.object({
  workflowId: z.string(),
  status: z.string(),
  startTime: z.string().optional(),
  result: z.unknown().optional(),
});

export async function startAIReview(payload: {
  ticket_id: string;
  board_id: string;
  personas?: string[];
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${payload.ticket_id}/boards/${payload.board_id}/ai-review`,
    {
      method: "POST",
      body: JSON.stringify({ personas: payload.personas }),
    },
  );

  return z.object({ workflowId: z.string(), status: z.string() }).parse(json);
}

export async function getAIReviewStatus(workflowId: string) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/ai-review/${workflowId}/status`,
    { method: "GET" },
  );

  return aiReviewStatusSchema.parse(json);
}

type AIReviewTarget = Pick<GenerateCommentsRequest, "ticket_id" | "board_id">;

type AIReviewPollOptions = {
  intervalMs: number;
  maxPolls: number;
  toleratedPollErrors: number;
  signal?: AbortSignal;
};

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new DOMException("AI review polling aborted", "AbortError");
  }
}

/**
 * Polls the review workflow until it reaches a terminal status. Resolves with
 * the latest review's comments, or `null` when every poll came back running.
 */
async function pollAIReview(
  workflowId: string,
  target: AIReviewTarget,
  { intervalMs, maxPolls, toleratedPollErrors, signal }: AIReviewPollOptions,
) {
  let consecutivePollErrors = 0;

  for (let i = 0; i < maxPolls; i++) {
    throwIfAborted(signal);
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    throwIfAborted(signal);

    let status: z.infer<typeof aiReviewStatusSchema>;
    try {
      status = await getAIReviewStatus(workflowId);
      consecutivePollErrors = 0;
    } catch (error) {
      consecutivePollErrors += 1;
      if (consecutivePollErrors > toleratedPollErrors) {
        throw error;
      }
      continue;
    }

    if (status.status === "COMPLETED") {
      // Check if the workflow skipped because a lock was already held
      const result = status.result as Record<string, unknown> | undefined;
      if (result?.status === "skipped") {
        throw new ReviewAlreadyInProgressError(
          "There is already a review in progress on this ticket version. Please check back shortly.",
        );
      }

      if (result?.status === "failed") {
        throw new AIReviewFailedError(
          (result?.error as string) || "unknown error",
        );
      }

      // Comments are saved in MongoDB by the workflow.
      // Fetch them from the server.
      const allComments = await fetchTicketComments({
        ticketId: target.ticket_id,
        query: "",
      });

      // Filter to AI comments from this board, most recent review batch
      const boardAIComments = allComments
        .filter(
          (c) =>
            c.isAI &&
            c.boardType === "revisionBoard" &&
            c.boardId === target.board_id,
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

      const latestReviewId = boardAIComments[0]?.reviewId;
      const reviewComments = latestReviewId
        ? boardAIComments.filter((c) => c.reviewId === latestReviewId)
        : boardAIComments;

      return {
        totalComments: reviewComments.length,
        allComments: reviewComments,
        reviewId: latestReviewId || workflowId,
      };
    }

    if (
      status.status === "FAILED" ||
      status.status === "TERMINATED" ||
      status.status === "TIMED_OUT" ||
      status.status === "CANCELLED"
    ) {
      throw new Error(`AI review workflow ${status.status.toLowerCase()}`);
    }
  }

  return null;
}

// why: christo starts ReviewWorkflow with workflowExecutionTimeout "1h"; 240 polls 15s apart cover that whole hour
const FOLLOW_POLL_INTERVAL_MS = 15_000;
const FOLLOW_MAX_POLLS = 240;
// why: an hour of polling meets the odd 5xx; one blip must not report a review that is still running as failed
const FOLLOW_TOLERATED_POLL_ERRORS = 3;

/**
 * Keeps following a review `requestAIComment` gave up on with
 * `ReviewStillRunningError`, until the workflow finishes or its hour is up.
 */
export async function followAIReview(
  { workflowId, ...target }: AIReviewTarget & { workflowId: string },
  signal?: AbortSignal,
) {
  const result = await pollAIReview(workflowId, target, {
    intervalMs: FOLLOW_POLL_INTERVAL_MS,
    maxPolls: FOLLOW_MAX_POLLS,
    toleratedPollErrors: FOLLOW_TOLERATED_POLL_ERRORS,
    signal,
  });
  if (result == null) {
    throw new Error("AI review did not finish within an hour");
  }
  return result;
}

// why: christo's status route answers the odd 5xx mid-review; one blip must not fail a review that is still running, and the 409 lock stays held
const FOREGROUND_TOLERATED_POLL_ERRORS = 3;

export async function requestAIComment(
  payload: GenerateCommentsRequest,
  signal?: AbortSignal,
) {
  try {
    // Start AI review via Christo → Temporal workflow
    const { workflowId } = await startAIReview(payload);

    const result = await pollAIReview(workflowId, payload, {
      intervalMs: 3000,
      maxPolls: 120, // ~6 minutes
      toleratedPollErrors: FOREGROUND_TOLERATED_POLL_ERRORS,
      signal,
    });
    if (result != null) {
      return result;
    }

    // Every poll returned a non-terminal status or a tolerated failure (a 4th
    // failing poll in a row throws in pollAIReview), so the workflow is still
    // running on the backend.
    throw new ReviewStillRunningError(
      "AI review is still running after 6 minutes",
      workflowId,
    );
  } catch (error: any) {
    if (
      error?.status === 409 ||
      error?.response?.status === 409 ||
      error?.statusCode === 409
    ) {
      throw new ReviewAlreadyInProgressError(
        "There is already a review in progress on this ticket version. Please check back shortly.",
      );
    }
    throw error;
  }
}

export async function checkReviewStatus({
  ticket_id,
  board_id,
}: {
  ticket_id: string;
  board_id: string;
}) {
  // Check the revision board's isRunningAIReview flag via the ticket endpoint
  const ticket = await getProject(ticket_id);
  const board = (ticket as any).revisionBoards?.find(
    (b: any) => b._id === board_id,
  );

  return {
    ticket_id,
    board_id,
    is_review_in_progress: board?.isRunningAIReview ?? false,
  };
}

export async function requestAskPunttComment(payload: AskPunttRequest) {
  const json = await smartFetch(
    `${LEGACY_AI_URL}/?type=${AIType.ASK_PUNTT}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    true,
  );

  const parsedData = askPunttResponseSchema.parse(json);
  return parsedData;
}

export async function createRevision({
  ticketId,
  payload,
}: {
  ticketId: string;
  payload: AddBoardToRevisionBoardBody;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/revisions`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  const parsedData = addBoardToRevisionBoardResponseSchema.parse(json);
  return parsedData;
}

export async function updateRevision({
  ticketId,
  boardId,
  payload,
}: {
  ticketId: string;
  boardId: string;
  payload: Record<string, unknown>;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/revisions`,
    {
      method: "PUT",
      body: JSON.stringify({
        _id: boardId,
        ...payload,
      }),
    },
  );

  const parsedData = updateRevisionBoardResponseSchema.parse(json);
  return parsedData;
}

export async function deleteRevision(ticketId: string, revisionId: string) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/revisions`,
    {
      method: "DELETE",
      body: JSON.stringify({
        boardId: revisionId,
      }),
    },
  );

  const parsedData = softDeleteRevisionBoardResponseSchema.parse(json);
  return parsedData;
}

export async function addReviewersToTicket({
  ticketId,
  payload,
}: {
  ticketId: string;
  payload: {
    reviewers: string[];
    note?: string;
  };
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/revisions/reviewer`,
    {
      method: "POST",
      body: JSON.stringify({
        ...payload,
      }),
    },
  );

  const parsedData = addReviewerToRevisionBoardResponseSchema.parse(json);
  return parsedData;
}

export async function approveTicket({ ticketId }: { ticketId: string }) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/approve`,
    {
      method: "POST",
    },
  );

  const parsedData = updateTicketResponseSchema.parse(json);
  return parsedData;
}

export async function reviewTicket({ payload }: { payload: ReviewTicketBody }) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/review-design`,
    {
      method: "POST",
      body: JSON.stringify({
        ...payload,
      }),
    },
  );

  const parsedData = reviewTicketResponseSchema.parse(json);
  return parsedData;
}

export async function sendToDesign(
  payload: SendToDesignBody & { ticketId: string },
) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${payload.ticketId}/send-to-design`,
    {
      method: "POST",
      body: JSON.stringify({
        creatives: payload.creatives,
        note: payload.note,
      }),
    },
  );

  const parsedData = sendToDesignResponseSchema.parse(json);
  return parsedData;
}

export async function getPromptFromTitle(title: string) {
  const json = await smartFetch(
    `${LEGACY_AI_URL}/?type=${AIType.TICKET_PROMPT}`,
    {
      method: "POST",
      body: JSON.stringify({ title }),
    },
    true,
  );

  const parsedData = generatePromptResponseSchema.parse(json);
  return parsedData;
}

export async function fetchDownloadableRevisionAsset({
  ticketId,
  boardId,
}: {
  ticketId: string;
  boardId: string;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/boards/${boardId}/download-delivery`,
    {
      method: "GET",
    },
  );

  const parsedData = getDownloadDeliveryResponseSchema.parse(json);
  return parsedData;
}

export async function fetchRecommendedCreatives(ticketId: string) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/suggest-creatives`,
    {
      method: "GET",
    },
  );

  const parsedData = suggestCreativesResponseSchema.parse(json);
  return parsedData;
}

export async function fetchRecommendedReviewers(ticketId: string) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/suggest-reviewers`,
    {
      method: "GET",
    },
  );

  const parsedData = suggestReviewersResponseSchema.parse(json);
  return parsedData;
}

export async function shareTicket({
  emails,
  ticketId,
}: {
  emails: string[];
  ticketId: string;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/share`,
    {
      method: "POST",
      body: JSON.stringify({
        emails,
      }),
    },
  );

  const parsedData = shareTicketResponseSchema.parse(json);
  return parsedData;
}

export async function deleteTicket({ ticketId }: { ticketId: string }) {
  await smartFetch(`${API_URL}/private/catalyst/tickets/${ticketId}`, {
    method: "DELETE",
  });

  return undefined;
}

export async function getParticipants(ticketOrFolderId?: string) {
  let token = null;

  if (ticketOrFolderId) {
    token = sessionStorage.getItem(`${ticketOrFolderId}|token`);
  }

  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/participants`,
    {
      method: "GET",
    },
    false,
    { token },
  );

  const parsedData = parseResponse(ticketParticipantsSchema, json);
  return parsedData;
}

export async function getFoldersAndTickets(
  params: QueryParams,
  folderId?: string,
): Promise<GetFolderTicketsPaginatedResponse> {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== undefined),
  );

  // Create URLSearchParams and handle arrays specially
  const searchParams = new URLSearchParams();
  Object.entries(filteredParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // For arrays, add multiple entries with the same key
      value.forEach((item) => searchParams.append(key, String(item)));
    } else {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  const urlToken = localStorage.getItem("urlToken");

  const tokenParam = urlToken ? `t=${urlToken}` : "";

  const separator = queryString ? "&" : "?";
  const tokenQueryString = tokenParam ? `${separator}${tokenParam}` : "";

  const url =
    folderId != null
      ? `${API_URL}/private/catalyst/folders/${folderId}?${queryString}${tokenQueryString}`
      : `${API_URL}/private/catalyst/folders?${queryString}`;

  const json = await smartFetch(
    url,
    {
      method: "GET",
    },
    false,
    { token: sessionStorage.getItem(`${folderId}|token`) },
  );

  // Same step arm as `getProject` above, on the sibling route the share-link
  // auto-switch is also mounted on (`GET /private/catalyst/folders/:id`), and
  // the same two reasons for reading it first: the listing schema rejects a
  // step body, and `urlToken` has to survive until the step completes.
  if (isEnterpriseMfaStep(json)) {
    parkEnterpriseMfaStep(json);
    throw new EnterpriseMfaStepParkedError(json);
  }

  localStorage.removeItem("urlToken");
  const parsedData = getFolderTicketsPaginatedResponseSchema.parse(json);
  return parsedData;
}

export async function createFolder(payload: CreateFolderBody) {
  const json = await smartFetch(`${API_URL}/private/catalyst/folders`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const parsedData = createFolderResponseSchema.parse(json);
  return parsedData;
}

export async function deleteFolder(folderId: string) {
  await smartFetch(`${API_URL}/private/catalyst/folders/${folderId}`, {
    method: "DELETE",
  });

  return undefined;
}

export async function updateFolder(
  payload: UpdateFolderBody & { _id: string },
) {
  const { _id, ...rest } = payload;
  await smartFetch(`${API_URL}/private/catalyst/folders/${_id}`, {
    method: "PUT",
    body: JSON.stringify(rest),
  });

  return undefined;
}

export async function shareTicket2(
  payload: ShareTicketBody & ShareTicketParams,
) {
  const { ticketId, emails, version } = payload;

  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/share`,
    {
      method: "POST",
      body: JSON.stringify({
        emails,
        version:
          version === "all" || version === "new" ? version : Number(version),
      }),
    },
  );

  const parsedData = parseResponse(shareTicketResponseSchema, json);
  return parsedData;
}

export async function getDownloadTicketLink({
  ticketId,
  boardId,
}: {
  ticketId: string;
  boardId?: string;
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/tickets/${ticketId}/download${
      boardId ? `?boardId=${encodeURIComponent(boardId)}` : ""
    }`,
    {
      method: "GET",
    },
  );

  const parsedData = parseResponse(downloadTicketResponseSchema, json);
  return parsedData;
}

export async function combineTickets({
  payload,
}: {
  payload: CombineTicketsBody;
}) {
  const json = await smartFetch(`${API_URL}/private/catalyst/tickets/combine`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json;
}

export async function getFolder(folderId: string) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/folders/${folderId}/details`,
    {
      method: "GET",
    },
  );

  const parsedData = parseResponse(getFolderDetailsResponseSchema, json);
  return parsedData;
}

// Integration response functions
export async function generateIntegrationSummary({
  ticketId,
  action,
}: {
  ticketId: string;
  action?: "accept" | "reject";
}) {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/integrations/summarize`,
    {
      method: "POST",
      body: JSON.stringify({ ticketId, action }),
    },
  );

  return json;
}

export async function submitIntegrationSummary({
  ticketId,
  adjustedSummary,
  action,
}: {
  ticketId: string;
  adjustedSummary?: string[] | null;
  action?: "accept" | "reject";
}) {
  const response = await fetch(
    `${API_URL}/private/catalyst/integrations/submit-summary`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthToken() || "",
      },
      body: JSON.stringify({ ticketId, adjustedSummary, action }),
    },
  );

  if (!response.ok) {
    throw response;
  }

  // Don't expect JSON response, just check if it was successful
  return { success: true };
}

// @ts-expect-error TS2339: `window` is strictly typed
window.UnsafeDeps = Object.assign({}, window.UnsafeDeps, {
  ticketApis: {
    getFoldersAndTickets,
    getTicket: getProject,
    getTicketVersionStatus,
    getDownloadTicketLink,
    createTicket: createProject,
    updateTicket: updateProject,
    shareTicket: shareTicket2,
    deleteTicket,
    getComments: fetchTicketComments,
    createComment,
    updateComment: editComment,
    deleteCommentMessage,
    requestAIComment,
    startAIReview,
    getAIReviewStatus,
    checkReviewStatus,
    requestAskPunttComment,
    createRevision,
    updateRevision,
    deleteRevision,
    getParticipants,
    createFolder,
    updateFolder,
    deleteFolder,
    combineTickets,
    getFolder,
  },
});
